/**
 * The two apps.
 *
 * Content about the products, in one place, so the hero, the download list and the
 * pricing cards cannot drift apart. Both entries describe what the app actually is:
 * read from each project's README rather than invented for a page.
 *
 * ## What is a fact and what is a sentence
 *
 * Each entry is split in two. `name`, `bundleId` and `shortcut` are facts about a
 * product: they are the same in every language, and `name` is a trademark that
 * translating it would turn into three spellings of one thing.
 *
 * Everything else is prose, and prose is stored once per language under `copy`. The
 * three sets live side by side in this file rather than in three files because they
 * are read together, edited together, and are only meaningfully correct as a set: a
 * feature list that gained a bullet in one language and not the other two is the
 * exact failure this layout makes obvious on sight.
 *
 * `appsFor(locale)` resolves one of the three into the flat shape the components
 * read. Nothing else in the app is allowed to reach past it into `copy`.
 */

import {
  Command,
  FileText,
  GitBranch,
  PanelLeft,
  Sparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react';

import type { Locale } from '../i18n/locale';
import type { AppCopy } from '../i18n/types';

export type AppId = 'orobas_hipercube' | 'orobas_work';

interface FeatureIdentity {
  readonly id: string;
  readonly icon: LucideIcon;
}

export interface AppFacts {
  readonly id: AppId;
  readonly name: string;
  readonly bundleId: string;
  /** The global shortcut that opens it, where it has one. */
  readonly shortcut: string | null;
  readonly role: string;
  readonly tagline: string;
  readonly summary: string;
  readonly requirements: string;
  readonly features: readonly {
    readonly id: string;
    readonly icon: LucideIcon;
    readonly title: string;
    readonly detail: string;
  }[];
}

interface AppEntry {
  readonly id: AppId;
  readonly name: string;
  readonly bundleId: string;
  readonly shortcut: string | null;
  /**
   * The icon belongs to the feature rather than to its position in a list, so a
   * feature and its icon cannot drift apart when the list is reordered or translated.
   * Identity and prose are held in parallel arrays here only because they are three
   * lists the same length, which `featureIcons` below asserts.
   */
  readonly copy: Readonly<Record<Locale, AppCopy>>;
}

const AGENT_FEATURES = [
  { id: 'floating-window', icon: Sparkles },
  { id: 'real-tools', icon: Zap },
  { id: 'providers', icon: Sparkles },
  { id: 'skills', icon: FileText },
  { id: 'multi-display', icon: PanelLeft },
  { id: 'local', icon: Sparkles },
] as const satisfies readonly FeatureIdentity[];

const WORK_FEATURES = [
  { id: 'liquid-glass', icon: PanelLeft },
  { id: 'real-editor', icon: FileText },
  { id: 'explorer', icon: GitBranch },
  { id: 'agent-tab', icon: Command },
  { id: 'panels', icon: PanelLeft },
  { id: 'one-session', icon: Command },
] as const satisfies readonly FeatureIdentity[];

export const APPS: readonly AppEntry[] = [
  {
    id: 'orobas_hipercube',
    name: 'OrobasHipercube',
    bundleId: 'com.pedromateus.orobasagent',
    shortcut: '⌘⇧O',
    copy: {
      'pt-BR': {
        role: 'Agente de IA',
        tagline: 'O agente de IA super poderoso que vive na sua barra de menus',
        summary:
          'Uma janela flutuante de IA que fica sobre o seu trabalho, com acesso real a arquivos, shell e git. Em fase beta.',
        requirements: 'macOS 26 ou posterior',
        features: [
          {
            title: 'Janela flutuante',
            detail:
              'Um ícone de 72×72 sempre visível, arrastável, que se prende às bordas. Abre com ⌘⇧O de qualquer lugar, ou com ⌘+clique no próprio ícone.',
          },
          {
            title: 'Agente com ferramentas de verdade',
            detail:
              'Lê, escreve e edita arquivos, executa comandos de shell, opera git e roda builds. Não é um chat que sugere código: ele age sobre o seu projeto.',
          },
          {
            title: 'Doze provedores gratuitos',
            detail:
              'OpenCode Zen, OpenRouter, Groq, Gemini, Cerebras, NVIDIA NIM, Mistral, GitHub Models, Hugging Face, ModelScope, SambaNova e Chutes, com busca dos modelos disponíveis.',
          },
          {
            title: 'Skills de engenharia',
            detail:
              'Guias práticos injetados no prompt a cada turno, editáveis como markdown: testes, depuração, revisão, planejamento e mais.',
          },
          {
            title: 'Multitela de verdade',
            detail:
              'Arraste o ícone entre monitores e leve a conversa ao próximo com ⌥⌘]. Desconectar um monitor nunca deixa uma janela fora da área de trabalho.',
          },
          {
            title: 'Local por opção',
            detail:
              'Roda com Ollama na sua máquina, sem enviar nada para fora. A chave da API, quando existe, fica no seu Keychain.',
          },
        ],
      },
      en: {
        role: 'AI agent',
        tagline: 'The super powerful AI agent that lives in your menu bar',
        summary:
          'A floating AI window that stays over your work, with real access to files, shell and git. Currently in beta.',
        requirements: 'macOS 26 or later',
        features: [
          {
            title: 'Floating window',
            detail:
              'A 72×72 icon that is always visible, draggable, and snaps to screen edges. Opens with ⌘⇧O from anywhere, or with ⌘-click on the icon itself.',
          },
          {
            title: 'An agent with real tools',
            detail:
              'Reads, writes and edits files, runs shell commands, drives git and executes builds. It is not a chat that suggests code: it acts on your project.',
          },
          {
            title: 'Twelve free providers',
            detail:
              'OpenCode Zen, OpenRouter, Groq, Gemini, Cerebras, NVIDIA NIM, Mistral, GitHub Models, Hugging Face, ModelScope, SambaNova and Chutes, with a search across the models each one offers.',
          },
          {
            title: 'Engineering skills',
            detail:
              'Practical guides injected into the prompt on every turn, editable as markdown: tests, debugging, review, planning and more.',
          },
          {
            title: 'Real multi-display',
            detail:
              'Drag the icon between displays and carry the conversation to the next one with ⌥⌘]. Unplugging a display never leaves a window outside the desktop.',
          },
          {
            title: 'Local when you want it',
            detail:
              'Runs against Ollama on your machine, with nothing sent out. The API key, when there is one, stays in your Keychain.',
          },
        ],
      },
      es: {
        role: 'Agente de IA',
        tagline: 'El agente de IA super poderoso que vive en tu barra de menús',
        summary:
          'Una ventana flotante de IA que se mantiene sobre tu trabajo, con acceso real a archivos, shell y git. Actualmente en beta.',
        requirements: 'macOS 26 o posterior',
        features: [
          {
            title: 'Ventana flotante',
            detail:
              'Un icono de 72×72 siempre visible, arrastrable, que se ancla a los bordes. Se abre con ⌘⇧O desde cualquier lugar, o con ⌘+clic en el propio icono.',
          },
          {
            title: 'Un agente con herramientas reales',
            detail:
              'Lee, escribe y edita archivos, ejecuta comandos de shell, maneja git y lanza builds. No es un chat que sugiere código: actúa sobre tu proyecto.',
          },
          {
            title: 'Doce proveedores gratuitos',
            detail:
              'OpenCode Zen, OpenRouter, Groq, Gemini, Cerebras, NVIDIA NIM, Mistral, GitHub Models, Hugging Face, ModelScope, SambaNova y Chutes, con búsqueda de los modelos disponibles.',
          },
          {
            title: 'Habilidades de ingeniería',
            detail:
              'Guías prácticas inyectadas en el prompt en cada turno, editables en markdown: pruebas, depuración, revisión, planificación y más.',
          },
          {
            title: 'Multipantalla de verdad',
            detail:
              'Arrastra el icono entre monitores y lleva la conversación al siguiente con ⌥⌘]. Desconectar un monitor nunca deja una ventana fuera del escritorio.',
          },
          {
            title: 'Local si quieres',
            detail:
              'Funciona con Ollama en tu equipo, sin enviar nada fuera. La clave de la API, cuando existe, queda en tu Keychain.',
          },
        ],
      },
    },
  },
  {
    id: 'orobas_work',
    name: 'Orobas Work',
    bundleId: 'com.pedromateus.orobaswork',
    shortcut: null,
    copy: {
      'pt-BR': {
        role: 'IDE',
        tagline: 'A IDE nativa com o agente a um clique',
        summary:
          'Uma IDE em Swift e SwiftUI, com Liquid Glass, que entrega o trabalho ao OrobasHipercube. Em fase beta.',
        requirements: 'macOS 26 ou posterior',
        features: [
          {
            title: 'Janela de vidro líquido',
            detail:
              'Cada painel é translúcido e a parede de fundo continua visível por trás de todos eles. Uma folha de vidro sobre a sua área de trabalho.',
          },
          {
            title: 'Editor de verdade',
            detail:
              'Realce de sintaxe, mapa de linhas, abas com estado de alteração, e diff para aceitar as mudanças do agente por trecho ou inteiras.',
          },
          {
            title: 'Explorador e controle de versão',
            detail:
              'Árvore de projetos, busca com filtro, indicador de git por arquivo e histórico, e outline de símbolos.',
          },
          {
            title: 'O Agent em uma aba',
            detail:
              'A aba Agent entrega o arquivo aberto, a posição do cursor e a seleção ao OrobasHipercube, no projeto que está aberto no editor.',
          },
          {
            title: 'Painéis de trabalho',
            detail:
              'Terminal, problemas, saída, build e ferramentas, em uma doca inferior que se reorganiza conforme o trabalho.',
          },
          {
            title: 'Uma sessão, dois apps',
            detail:
              'Entrar no Orobas Work entra no OrobasHipercube. A sessão vive em um arquivo só e é anunciada entre os processos, então o outro app assume a sessão sem precisar entrar de novo.',
          },
        ],
      },
      en: {
        role: 'IDE',
        tagline: 'The native IDE with the agent one click away',
        summary:
          'An IDE in Swift and SwiftUI, with Liquid Glass, that hands the work to OrobasHipercube. Currently in beta.',
        requirements: 'macOS 26 or later',
        features: [
          {
            title: 'A liquid glass window',
            detail:
              'Every panel is translucent and the wallpaper stays visible behind all of them. A sheet of glass laid over your desktop.',
          },
          {
            title: 'A real editor',
            detail:
              'Syntax highlighting, a minimap, tabs that track unsaved changes, and a diff that accepts the agent’s edits by hunk or in full.',
          },
          {
            title: 'Explorer and version control',
            detail:
              'Project tree, filtered search, per-file and per-history git state, and a symbol outline.',
          },
          {
            title: 'The Agent in a tab',
            detail:
              'The Agent tab hands the open file, the cursor position and the selection to OrobasHipercube, in the project the editor has open.',
          },
          {
            title: 'Work panels',
            detail:
              'Terminal, problems, output, build and tools, in a bottom dock that rearranges itself as the work changes.',
          },
          {
            title: 'One session, two apps',
            detail:
              'Signing in to Orobas Work signs in to OrobasHipercube. The session lives in one file and is announced between the processes, so the other app picks it up without signing in again.',
          },
        ],
      },
      es: {
        role: 'IDE',
        tagline: 'La IDE nativa con el agente a un clic',
        summary:
          'Una IDE en Swift y SwiftUI, con Liquid Glass, que le entrega el trabajo a OrobasHipercube. Actualmente en beta.',
        requirements: 'macOS 26 o posterior',
        features: [
          {
            title: 'Ventana de cristal líquido',
            detail:
              'Cada panel es translúcido y el papel pintado sigue viéndose por detrás de todos ellos. Una hoja de cristal sobre tu escritorio.',
          },
          {
            title: 'Un editor de verdad',
            detail:
              'Resaltado de sintaxis, minimapa, pestañas con estado de cambios y diff para aceptar las ediciones del agente por fragmento o completas.',
          },
          {
            title: 'Explorador y control de versiones',
            detail:
              'Árbol de proyectos, búsqueda con filtro, estado de git por archivo e historial, y esquema de símbolos.',
          },
          {
            title: 'El Agent en una pestaña',
            detail:
              'La pestaña Agent entrega el archivo abierto, la posición del cursor y la selección a OrobasHipercube, en el proyecto que el editor tiene abierto.',
          },
          {
            title: 'Paneles de trabajo',
            detail:
              'Terminal, problemas, salida, build y herramientas, en un dock inferior que se reorganiza según el trabajo.',
          },
          {
            title: 'Una sesión, dos aplicaciones',
            detail:
              'Entrar en Orobas Work entra en OrobasHipercube. La sesión vive en un solo archivo y se anuncia entre los procesos, así que la otra aplicación la asume sin volver a entrar.',
          },
        ],
      },
    },
  },
];

/**
 * The two apps, resolved into one language.
 *
 * Every call returns a fresh array, so a caller that filters or sorts cannot write
 * back into the catalogue. The copy arrays are read-only by type, but the objects
 * inside them are shared, and a `sort()` in one component would reorder them for the
 * next render of another.
 */
export function appsFor(locale: Locale): readonly AppFacts[] {
  return APPS.map((entry) => {
    const copy = entry.copy[locale];
    const icons = entry.id === 'orobas_hipercube' ? AGENT_FEATURES : WORK_FEATURES;

    // Three lists of the same length that only make sense together. A feature added
    // to one language and not the others is caught here, at render, rather than by a
    // reader looking at a card with four bullets where the English one has six.
    if (copy.features.length !== icons.length) {
      throw new Error(
        `orobas: ${entry.id} has ${icons.length} feature icons but ${copy.features.length} entries in ${locale}`,
      );
    }

    return {
      id: entry.id,
      name: entry.name,
      bundleId: entry.bundleId,
      shortcut: entry.shortcut,
      role: copy.role,
      tagline: copy.tagline,
      summary: copy.summary,
      requirements: copy.requirements,
      features: copy.features.map((feature, index) => {
        // The lengths were checked above, so this cannot happen. It is checked again
        // because the alternative is `icons[index]!`, which is a promise about an
        // array index rather than a fact the compiler can check.
        const identity = icons[index];
        if (identity === undefined) {
          throw new Error(`orobas: ${entry.id} feature ${index} has no icon in ${locale}`);
        }
        return {
          id: identity.id,
          icon: identity.icon,
          title: feature.title,
          detail: feature.detail,
        };
      }),
    };
  });
}

export function appFactsById(id: string, locale: Locale): AppFacts | undefined {
  return appsFor(locale).find((app) => app.id === id);
}

/** The product id that unlocks both. */
export const SUITE_PRODUCT_ID = 'orobas_suite';